import React, { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { Routes, Route, useLocation } from 'react-router-dom';

import "./i18n";
import { routes } from './routes';
import Layout from './components/Layout';
import { exchangeRateState } from './store/exchangeRateAtom';
import { cartState } from './store';
import { list as fetchExchangeRates } from './api/exchangeRateApi';

// Pages
import Home from './modules/home/Main';
import ShowCollection from './modules/collections/Show';
import Works from './modules/works/Main';
import ShowWork from './modules/works/Show';
import Cart from './modules/cart/Cart';
import TermsAndConidions from './modules/terms_and_conditions/TermsAndConditions'
import FAQ from './modules/faq/FAQ';
import PaymentSuccess from './modules/cart/PaymentSuccess';
import WorksByYear from './modules/collections/WorksByYear';
import PrivacyPolicy from './modules/privacy_policy/PrivacyPolicy';

// Errors
import NotFound from './modules/errors/NotFound';

// Analytics
import { initGA, trackPageView } from './analytics';
import WhatsAppButton from './components/WhatsAppButton';
import Contact from './modules/contact/Contact';
import Articles from './modules/articles/Articles';
import { Helmet } from 'react-helmet-async';

function App() {
  const location = useLocation();

  const setExchangeRates = useSetRecoilState(exchangeRateState);
  const setCart = useSetRecoilState(cartState);

  useEffect(() => {
    initGA() // initialize google analytics
  }, [])

  useEffect(() => {
    trackPageView(location.pathname + location.search) // track on route change
  }, [location])

  useEffect(() => {
    const getExchangeRates = async () => {
      const response = await fetchExchangeRates();
      setExchangeRates(response);
    };
    getExchangeRates();

    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart)); // Initialize cart state
    }

  }, [setExchangeRates, setCart]);

  return (
    <>
      <Helmet>
        <title>Eduard Belsky | Online Art Gallery</title>
        <meta name="description" content="Discover and buy original paintings by Eduard Belsky. Explore the catalog, view works in AR, and subscribe for updates." />
        <meta
          name="keywords"
          content="Eduard Belsky, contemporary art for sale, original artwork online, buy original paintings, ceramic art for sale, handmade ceramics, modern art gallery, European artist, art for collectors, unique home decor, fine art online store, buy art online Europe, affordable original art, art shipping worldwide, Slovenian artist, oil paintings for sale, abstract art painting, contemporary oil on canvas, original acrylic painting, expressive art, figurative painting, modern abstract painting, large wall art, landscape painting, narrative art, handmade ceramic sculpture, collectible ceramics, art pottery for sale, unique ceramic pieces, decorative ceramics, clay art, ceramic wall art, artisan pottery online, buy art with international shipping, safe worldwide art delivery, art packaging and delivery, order art online from Europe, online art shop Slovenia, where to buy original art online in Europe, how to buy affordable fine art from artists, Slovenian artist selling paintings and ceramics, one-of-a-kind ceramic art shipped internationally, shop for handmade art directly from artist, unique gifts for art lovers, investment in original contemporary art, modern decorative art for interiors"
        />
        <link rel="canonical" href="https://eduardbelsky.com/" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Eduard Belsky | Online Art Gallery" />
        <meta property="og:description" content="Discover and buy original paintings by Eduard Belsky. Explore the catalog, view works in AR, and subscribe for updates." />
        <meta property="og:image" content="https://eduardbelsky.com/images/og-home.jpg" />
        <meta property="og:url" content="https://eduardbelsky.com/" />

        <script type="application/ld+json">
          {`
				{
				"@context": "https://schema.org",
				"@type": "Person",
				"name": "Eduard Belsky",
				"jobTitle": "Artist",
				"url": "https://eduardbelsky.com/",
				"sameAs": [
					"https://www.facebook.com/eduard.belsky/"
				]
				}
				`}
        </script>
      </Helmet>
      <WhatsAppButton />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route
            index
            element={
              <Home />
            }
          />
          <Route
            path={routes.error.notFound}
            element={
              <NotFound />
            }
          />
          <Route
            path={routes.newWorks.index}
            element={<WorksByYear />}
          />
          <Route
            path={routes.collections.show()}
            element={
              <ShowCollection />
            }
          />
          <Route
            path={routes.works.index}
            element={
              <Works />
            }
          />
          <Route
            path={routes.works.show()}
            element={
              <ShowWork />
            }
          />
          <Route
            path={routes.cart.index}
            element={
              <Cart />
            }
          />
          <Route
            path={routes.privacyPolicy.index}
            element={
              <PrivacyPolicy />
            }
          />
          <Route
            path={routes.termsAndConditions.index}
            element={
              <TermsAndConidions />
            }
          />
          <Route
            path={routes.checkout.success}
            element={
              <PaymentSuccess />
            }
          />
          <Route
            path={routes.faq.index}
            element={
              <FAQ />
            }
          />
          <Route
            path={routes.contact.index}
            element={
              <Contact />
            }
          />
          <Route
            path={routes.articles.index}
            element={<Articles />}
          />
        </Route>
      </Routes>
    </>
  );
}

export default App;